import { WorkflowReviewLookupRequestForm } from '@app/generated/contracts/workflow-review-lookup-request.generated';
import { applyWorkflowFormConfiguration } from '@app/shared/workflow-commands/workflow-form-configuration';
import {
  isWorkflowFormLookupBinding,
  workflowFormLookupBindingKey,
} from '@app/shared/workflow-commands/workflow-form-lookup-contract';
import type { GeneratedFormDefinition } from '@template-ui/helpers/generated-form.helpers';
import type {
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
} from './platform-workflow-definition-lifecycle.models';
import { sameCapabilityReference } from './workflow-capability-authoring';
import {
  hasInstalledReviewRequestLookupRuntime,
  workflowFormFieldIssues,
} from './workflow-form-fields-validation';
import type { StepAuthoringCatalog } from './workflow-step-composition';
import {
  isReviewRequestLookupCommand,
  reviewRequestLookupPairReason,
} from './workflow-review-request-lookup-authoring-gate';

export interface Request2LocalLookupSample {
  kind: 'local-example';
  namespace: string;
  command: WorkflowCapabilityReference;
  bindingKey: string;
  configurationCode: string;
  domainCode: string;
  unavailableIssue: string;
  form: GeneratedFormDefinition;
}

/** Artificial presentation only; no offers, identities, request transport or evidence enter this model. */
export function request2LocalLookupSample(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  catalog: StepAuthoringCatalog | null,
): Request2LocalLookupSample | null {
  if (
    document.payloadSchemaVersion !== '1.21' ||
    !Array.isArray(document.nodes) ||
    !catalog ||
    !Array.isArray(catalog.commands) ||
    !Array.isArray(catalog.interactions)
  )
    return null;
  const nodes = document.nodes.filter((node) => node?.id === nodeId),
    node = nodes[0];
  const commands = node?.declaration?.behavior?.commands;
  if (
    nodes.length !== 1 ||
    node.typeCode !== 'human_step' ||
    !Array.isArray(commands)
  )
    return null;
  const requests = commands.filter((command) =>
    isReviewRequestLookupCommand(command?.capability),
  );
  const installed = catalog.commands.filter((command) =>
    isReviewRequestLookupCommand(command?.capability),
  );
  if (requests.length !== 1 || installed.length !== 1) return null;
  const request = requests[0],
    contract = installed[0];
  if (
    !hasInstalledReviewRequestLookupRuntime(request, contract) ||
    reviewRequestLookupPairReason(document, nodeId, request, catalog, contract)
  )
    return null;
  const main = node.declaration?.detail?.mainInteraction;
  const interactions = catalog.interactions.filter((item) =>
    isReviewRequestLookupCommand(item?.command),
  );
  const configuration = request.formConfiguration;
  if (
    !main ||
    main.code !== 'generated-command-form' ||
    main.version !== 1 ||
    !sameCapabilityReference(main.command, request.capability) ||
    interactions.length !== 1 ||
    interactions[0].code !== main.code ||
    interactions[0].version !== main.version ||
    !sameCapabilityReference(interactions[0].command, request.capability) ||
    configuration?.version !== 3 ||
    !Array.isArray(configuration.fields) ||
    configuration.fields.length !== 1
  )
    return null;
  const setting = configuration.fields[0];
  if (
    setting?.name !== 'workSummary' ||
    setting.control !== 'select' ||
    setting.optionSource !== 'lookup' ||
    setting.options != null ||
    !isWorkflowFormLookupBinding(setting.lookup)
  )
    return null;
  const configured = applyWorkflowFormConfiguration(
    WorkflowReviewLookupRequestForm,
    configuration,
    { installedDefinition: WorkflowReviewLookupRequestForm },
  );
  if (!configured) return null;
  const issues = workflowFormFieldIssues(request, contract);
  if (issues.form || Object.keys(issues.fields).length) return null;
  const bindingKey = workflowFormLookupBindingKey(setting.lookup);
  return {
    kind: 'local-example',
    namespace: JSON.stringify([document, nodeId, catalog, bindingKey]),
    command: structuredClone(request.capability),
    bindingKey,
    configurationCode: setting.lookup.configurationCode,
    domainCode: setting.lookup.domainCode,
    unavailableIssue: `${contract.label}: sample execution and checks are unavailable for this request form.`,
    form: {
      ...structuredClone(configured),
      fields: configured.fields.map((field) => ({
        ...structuredClone(field),
        disabled: false,
        options: [
          { value: 'A', label: 'Artificial example A', labelToken: '' },
          { value: 'B', label: 'Artificial example B', labelToken: '' },
        ],
      })),
    },
  };
}
